import type { Session } from '@cyberscore/contracts';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { api, setSession, setUnauthorizedHandler } from './api';
import { tokens } from './tokens';

export interface Tenant {
  id: string;
  name: string;
  kind: 'platform' | 'insurer' | 'broker' | 'business' | string;
  roles: string[];
  /** What they may do here (task 78): the app shows and hides by this, never by role names. */
  permissions: string[];
  /** The colour its app shows (task 97): its own or the platform's; null for the built-in default. */
  brand: string | null;
}

/** Whether the person holds any one of these permissions in this tenant. */
export const can = (tenant: Tenant | null | undefined, ...permissions: string[]) =>
  !!tenant && permissions.some((p) => tenant.permissions.includes(p));

export interface Me {
  user: { id: string; email: string | null; name: string | null };
  tenants: Tenant[];
  /** The API's own sign-in: the account page applies. */
  session: { own: boolean; mfa: boolean };
  /** Staff of an insurer, broker or the platform must turn on two-step sign-in first. */
  mfa_required: boolean;
}

interface SessionValue {
  me: Me | null;
  tenant: Tenant | null;
  loading: boolean;
  /** A finished sign-in (password, link or second step): the access token it returned. */
  signedIn: (s: Session) => void;
  signInDev: (subject: string) => Promise<void>;
  switchTenant: (id: string) => void;
  signOut: () => void;
  /** Fetch who is signed in again, e.g. after joining a new organisation. */
  reload: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);
const TENANT_STORE = 'cyberscore.tenant';

/** Who is signed in (lib/tokens.ts keeps the token) and which tenant they are working in. */
export function SessionProvider({ children }: { children: ReactNode }) {
  const queries = useQueryClient();
  const { auth, restoring } = useSyncExternalStore(tokens.subscribe, tokens.get);
  const [tenantId, setTenantId] = useState<string | null>(() => readTenant());
  const [fetched, setFetched] = useState<{ id: number; me: Me } | null>(null);
  const me = auth && fetched?.id === auth.id ? fetched.me : null;
  const loading = restoring || (!!auth && fetched?.id !== auth.id);

  useEffect(() => tokens.restore(), []);

  // Set during render, not in an effect: children's queries run before this component's effects.
  setSession(auth?.token ?? null, tenantId);
  useEffect(() => writeTenant(tenantId), [tenantId]);

  const forget = useCallback(() => {
    queries.clear();
    setTenantId(null);
    setFetched(null);
  }, [queries]);

  const signOut = useCallback(() => {
    forget();
    tokens.signOut();
  }, [forget]);

  useEffect(
    () =>
      setUnauthorizedHandler(() => {
        // An expired access token: try the refresh cookie once before signing out.
        if (tokens.get().auth?.mode === 'account')
          void tokens.refresh().then((ok) => {
            if (!ok) signOut();
          });
        else signOut();
      }),
    [signOut],
  );

  const pick = useCallback((m: Me) => {
    setTenantId((current) =>
      current && m.tenants.some((t) => t.id === current) ? current : (m.tenants[0]?.id ?? null),
    );
  }, []);

  // Only a new sign-in fetches who it is again, not each refreshed token.
  const id = auth?.id ?? null;
  useEffect(() => {
    if (id === null) return;
    let live = true;
    api<Me>('GET', '/auth/me')
      .then((m) => {
        if (!live) return;
        setFetched({ id, me: m });
        pick(m);
      })
      .catch(() => live && signOut());
    return () => {
      live = false;
    };
  }, [id, pick, signOut]);

  const signedIn = useCallback(
    (s: Session) => {
      forget();
      tokens.signedIn(s);
    },
    [forget],
  );

  const signInDev = useCallback(
    async (subject: string) => {
      // Local development only: production builds have no stand-in issuer, and the API wouldn't trust one.
      if (!import.meta.env.DEV) throw new Error('Development sign-in is not available');
      const res = await fetch(`/dev-issuer/token?sub=${encodeURIComponent(subject)}`);
      if (!res.ok)
        throw new Error('The dev issuer is not running (pnpm --filter @cyberscore/core dev:issuer)');
      const { access_token } = (await res.json()) as { access_token: string };
      forget();
      tokens.signedInDev(access_token);
    },
    [forget],
  );

  const switchTenant = useCallback(
    (tid: string) => {
      queries.clear();
      setTenantId(tid);
    },
    [queries],
  );

  const reload = useCallback(async () => {
    if (id === null) return;
    const m = await api<Me>('GET', '/auth/me');
    setFetched({ id, me: m });
    pick(m);
  }, [id, pick]);

  const value = useMemo<SessionValue>(
    () => ({
      me,
      tenant: me?.tenants.find((t) => t.id === tenantId) ?? null,
      loading,
      signedIn,
      signInDev,
      switchTenant,
      signOut,
      reload,
    }),
    [me, tenantId, loading, signedIn, signInDev, switchTenant, signOut, reload],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(SessionContext);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
}

function readTenant(): string | null {
  try {
    return sessionStorage.getItem(TENANT_STORE);
  } catch {
    return null;
  }
}

function writeTenant(id: string | null) {
  try {
    if (id) sessionStorage.setItem(TENANT_STORE, id);
    else sessionStorage.removeItem(TENANT_STORE);
  } catch {
    /* storage unavailable */
  }
}
